import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { CustomerLoginInput, CustomerProfile, CustomerRegisterInput } from '@najah/core'
import { disconnectPush, getCurrentPushToken } from './pushClient'
import {
  api, getRefreshToken, setAccessToken, setRefreshToken, setSessionExpiredHandler, type AuthResponse,
} from './api'

interface AuthContextValue {
  customer: CustomerProfile | null
  isRestoring: boolean
  login: (input: CustomerLoginInput) => Promise<void>
  register: (input: CustomerRegisterInput) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null)
  const [isRestoring, setIsRestoring] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function restore() {
      if (!getRefreshToken()) {
        if (!cancelled) setIsRestoring(false)
        return
      }
      try {
        // أول طلب محميّ يُشغّل التحديث تلقائياً داخل عميل الـ API
        const me = await api<CustomerProfile>('/auth/me')
        if (!cancelled) setCustomer(me)
      } catch {
        if (!cancelled) setCustomer(null)
      } finally {
        if (!cancelled) setIsRestoring(false)
      }
    }

    void restore()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    setSessionExpiredHandler(() => { setCustomer(null); void disconnectPush(false) })
    return () => setSessionExpiredHandler(null)
  }, [])

  const apply = useCallback((res: AuthResponse) => {
    setAccessToken(res.accessToken)
    setRefreshToken(res.refreshToken)
    setCustomer(res.customer)
  }, [])

  const login = useCallback(async (input: CustomerLoginInput) => {
    apply(await api<AuthResponse>('/auth/login', { method: 'POST', body: input }))
  }, [apply])

  const register = useCallback(async (input: CustomerRegisterInput) => {
    apply(await api<AuthResponse>('/auth/register', { method: 'POST', body: input }))
  }, [apply])

  const logout = useCallback(async () => {
    const pushToken = getCurrentPushToken()
    await disconnectPush()
    try {
      await api('/auth/logout', { method: 'POST', body: { refreshToken: getRefreshToken(), pushToken: pushToken ?? undefined } })
    } catch {
      // الخروج محلياً يتم في كل الأحوال
    }
    setAccessToken(null)
    setRefreshToken(null)
    setCustomer(null)
  }, [])

  const value = useMemo(
    () => ({ customer, isRestoring, login, register, logout }),
    [customer, isRestoring, login, register, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth يجب أن يُستعمل داخل AuthProvider')
  return ctx
}
